import { RowTable } from "@/components/table/UITableProps";
import { Direccion, FileInfo, RepresentanteLegal } from "@/services/common/expediente-types";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";

/**
 * Cola de cotejo de un Representante Legal recién agregado (HU-4).
 *
 * Cuando el usuario sube los documentos de un RL nuevo en el modal
 * "Agregar Representante legal", se recorre una cadena de pantallas de cotejo
 * en orden fijo (Poder → Identificación → Residencia, esta última sólo para
 * extranjeros). Mientras `active` es true, cada pantalla `validate/*` avanza al
 * siguiente documento de la cola en vez de regresar al management.
 *
 * Si el campo es `undefined` (estado por defecto / no presente en initialState),
 * las pantallas se comportan exactamente como en el flujo single-doc de hoy.
 */
export interface RLCotejoChain {
  active: boolean;
  /** Cola ordenada de documentos a cotejar (Poder → Identificación → Residencia). */
  docs: FileInfo[];
  /** Índice del documento actual dentro de `docs`. */
  index: number;
  /** RL nuevo (placeholder sin id: lo creará el cotejo final en back). */
  representative: RepresentanteLegal;
  /**
   * Datos por documento mapeados con `getRequestByType` (EL MISMO mapeo que el
   * cotejo single-doc usa para enviar al back) e incluyendo las ediciones que el
   * usuario hizo en cada pantalla. Se ARRASTRAN pantalla a pantalla hasta el
   * cotejo final, que los manda como `datos` de cada documento.
   */
  collected?: Record<string, unknown>;
  /**
   * Estado CRUDO del formulario por documento (el `activeUser.data` de cada
   * pantalla con las ediciones del usuario). Permite la navegación "Anterior"
   * RE-EDITABLE (HU-4): al volver a un documento ya visitado se restauran SUS
   * datos capturados (no se re-parsea la extracción ni se pierden los cambios).
   * A diferencia de `collected` (shape de envío `getRequestByType`), aquí se
   * guarda el shape del FORM (incluye campos sólo-form como `confirmarCorreo`).
   */
  forms?: Record<string, unknown>;
  /**
   * Tipo REAL extraído de los documentos de BACKGROUND (identificación: INE_IFE o
   * PASAPORTE; residencia: INM). Se manda como `tipoDocumento` en el cotejo final.
   * El Poder se extrae en el modal y se manda con su tipo de slot (CARTA_PODER).
   */
  extractedType?: Record<string, string>;
  /**
   * Estado de la EXTRACCIÓN (processFile) por documento. Modelo híbrido: el
   * Poder se extrae en el modal (bloqueante); INE/Residencia corren en
   * background mientras el usuario revisa el Poder. `pending` = extrayendo,
   * `ready` = params listos, `failed` = no se pudo leer / no pasó las
   * validaciones post-extracción (tipo/sello/vigencia) — la cadena se aborta
   * avisando al usuario.
   */
  extraction?: Record<string, RLExtractionStatus>;
  /** Código de error (ErrorMap) de la 1ª extracción fallida en background. */
  extractionFailCode?: string;
}

export type RLExtractionStatus = "pending" | "ready" | "failed";

export interface Expediente {
  idProcess?: string;
  documentToValidate?: FileInfo;
  idDocument: string;
  pertenencia?: string;
  tipoEsperado?: string;
  address?: Direccion;
  listFiles?: RowTable[];
  representative?: RepresentanteLegal;
  /**
   * Cola de cotejo de un RL recién agregado (HU-4). Ausente/undefined ⇒ flujo
   * single-doc normal; presente y `active` ⇒ las pantallas `validate/*`
   * encadenan Poder → Identificación → Residencia. Se persiste en localStorage
   * por cliente igual que el resto del expediente y `cleanExpediente()` la borra
   * (no está en initialState). Ver [[RLCotejoChain]].
   */
  rlCotejoChain?: RLCotejoChain;
}

const initialState: Expediente = Object.freeze({
  idProcess: "",
  idDocument: "",
});

interface ExpedienteContextType {
  expediente: Expediente;
  setExpediente: React.Dispatch<React.SetStateAction<Expediente>>;
  idExpediente: string;
  setIdExpediente: React.Dispatch<React.SetStateAction<string>>;
  cleanExpediente: () => void;
}

const ExpedienteContext = createContext<ExpedienteContextType>({
  expediente: initialState,
  idExpediente: "",
  setExpediente: () => { },
  setIdExpediente: () => { },
  cleanExpediente: () => { },
});

const isEmptyExpediente = (expedient: Expediente) => expedient.idProcess === "";

const ExpedienteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [expediente, setExpediente] = useState<Expediente>(JSON.parse(JSON.stringify(initialState)));
  const [idExpediente, setIdExpediente] = useState<string>("");
  const [wasCleaned, setWasCleaned] = useState<boolean>(false);
  // Último (idExpediente, expediente) que vio el efecto de persistencia. Permite
  // detectar el commit en el que SOLO cambió idExpediente (navegación a otro
  // cliente): ahí el expediente en memoria aún es del cliente anterior y NO debe
  // escribirse bajo la key del nuevo (pisaría su blob antes de que el efecto de
  // carga lo lea). Cuando el caller setea id y expediente en el MISMO commit
  // (inicio de expediente nuevo en CustomerDetail) sí se escribe, conservando el
  // write-through del que depende ese flujo.
  const lastPersistedRef = useRef<{ id: string; exp: Expediente }>({ id: "", exp: expediente });

  const cleanExpediente = () => {
    console.log("Cleaning expedienteContext");
    if (idExpediente) {
      localStorage.setItem(`expediente-${idExpediente}`, JSON.stringify(initialState));
    }
    setExpediente(initialState); // También limpia el state local
    setIdExpediente("");
    setWasCleaned(true);
  };

  useEffect(() => {
    const idChanged = lastPersistedRef.current.id !== idExpediente;
    const expChanged = lastPersistedRef.current.exp !== expediente;
    lastPersistedRef.current = { id: idExpediente, exp: expediente };
    // Cambió el cliente sin que cambiara el estado en memoria → el estado es del
    // cliente ANTERIOR: no escribirlo bajo la key del nuevo. El efecto de carga
    // (abajo) hidratará el estado real de este cliente.
    if (idChanged && !expChanged) return;
    if (isEmptyExpediente(expediente) || idExpediente === "") return;
    localStorage.setItem(`expediente-${idExpediente}`, JSON.stringify(expediente));
  }, [expediente, idExpediente]);

  useEffect(() => {
    if (idExpediente === "" || wasCleaned) {
      if (wasCleaned) {
        setWasCleaned(false); // Resetear la bandera después de evitar la carga
      }
      return;
    }

    const storedData = localStorage.getItem(`expediente-${idExpediente}`);
    if (storedData && storedData !== "undefined" && storedData !== "null") {
      const dataExpediente = JSON.parse(storedData);
      setExpediente(dataExpediente);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idExpediente]);

  return <ExpedienteContext.Provider value={{ expediente, setExpediente, idExpediente, setIdExpediente, cleanExpediente, }} >{children}</ExpedienteContext.Provider>;
};

const useExpedienteContext = () => useContext(ExpedienteContext);

export { ExpedienteProvider, ExpedienteContext, useExpedienteContext };
